import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, ArrowRight, Flame } from "lucide-react";
import { Wordmark, LogoLockup } from "@/components/Logo";
import { api } from "@/lib/api";

function Nav() {
  return (
    <header className="px-6 md:px-8 py-5 flex items-center justify-between border-b" style={{ borderColor: "var(--border)" }}>
      <Link to="/" data-testid="pricing-nav-logo"><Wordmark /></Link>
      <nav className="hidden md:flex items-center gap-8 text-sm" style={{ color: "var(--text-dim)" }}>
        <Link to="/#features" className="hover:text-white">Features</Link>
        <Link to="/pricing" className="hover:text-white" style={{color:"var(--text)"}}>Pricing</Link>
        <Link to="/#academies" className="hover:text-white">Academies</Link>
      </nav>
      <RequestAccess />
    </header>
  );
}

function RequestAccess({ label = "Request access", testid = "cta-request-access" }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [academy, setAcademy] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => { e.preventDefault(); try { await api.post("/leads", { email, academy_name: academy, source: "pricing-cta" }); } catch (err) { console.error("Lead submission failed:", err); } setSent(true); };
  return (
    <>
      <button onClick={()=>setOpen(true)} className="btn btn-primary" data-testid={testid}>{label}</button>
      {open && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", display:"grid", placeItems:"center", zIndex:60 }} onClick={()=>setOpen(false)}>
          <div className="card p-7 w-[440px] max-w-[90vw]" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div><div className="label" style={{color:"var(--green)"}}>Founding academies</div><div className="text-[20px] font-bold uppercase mt-1 tracking-tight">Request access</div></div>
              <button onClick={()=>setOpen(false)} data-testid="req-close"><X size={18}/></button>
            </div>
            {sent ? (
              <div className="py-6 text-center" data-testid="req-sent">
                <div style={{color:"var(--green)"}} className="flex items-center justify-center gap-2"><Check size={18}/> Thanks — we'll reach out within 24h.</div>
                <button onClick={()=>{setOpen(false); setSent(false); setEmail(""); setAcademy("");}} className="btn btn-outline mt-5">Close</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <label className="block label">Academy name<input required className="input mt-1" value={academy} onChange={e=>setAcademy(e.target.value)} placeholder="Ludus Gladius BJJ" data-testid="req-academy"/></label>
                <label className="block label">Owner email<input required type="email" className="input mt-1" value={email} onChange={e=>setEmail(e.target.value)} placeholder="owner@academy.com" data-testid="req-email"/></label>
                <button type="submit" className="btn btn-primary w-full mt-2" data-testid="req-submit">Request access <ArrowRight size={14}/></button>
                <div className="label-tiny text-center mt-3">We're onboarding founding academies one-by-one.</div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const PLANS = [
  {
    name: "Tatame",
    sub: "For young academies",
    priceMonthly: 59,
    priceAnnual: 49,
    cap: "Up to 80 active students",
    features: [
      "Class schedule + reservations",
      "QR self check-in",
      "Belt tracking + promotion history",
      "Student mobile app · RollOSS-branded PWA",
      "Membership billing · Tilopay + Yappy · cash & transfer logging",
      "Email support",
    ],
  },
  {
    name: "Academy",
    sub: "For growing academies",
    priceMonthly: 119,
    priceAnnual: 99,
    cap: "Up to 300 active students",
    popular: true,
    features: [
      "Everything in Tatame",
      "Coach dashboards",
      "Announcements + push · belt & individual targeting",
      "Auto-recurring billing · receipts · past-due reminders",
      "Family profiles · parents manage multiple kids",
      "Priority support",
    ],
  },
  {
    name: "Federation",
    sub: "For large academies",
    priceMonthly: 299,
    priceAnnual: 249,
    cap: "Unlimited students",
    features: [
      "Everything in Academy",
      "Branded student app · your logo, colours & custom domain",
      "Multi-location switching + consolidated revenue analytics",
      "Dedicated onboarding",
      "SLA + phone support",
    ],
  },
];

function BillingToggle({ value, onChange }) {
  return (
    <div className="inline-flex items-center p-1 rounded-full" style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }} data-testid="billing-toggle">
      <button onClick={()=>onChange("monthly")} className="px-5 py-2 rounded-full text-[12px] font-semibold tracking-wider uppercase transition-colors"
        style={value==="monthly" ? { background: "var(--green)", color: "#062012" } : { color: "var(--text-dim)" }} data-testid="billing-monthly">
        Monthly
      </button>
      <button onClick={()=>onChange("annual")} className="px-5 py-2 rounded-full text-[12px] font-semibold tracking-wider uppercase flex items-center gap-2 transition-colors"
        style={value==="annual" ? { background: "var(--green)", color: "#062012" } : { color: "var(--text-dim)" }} data-testid="billing-annual">
        Annual
        <span className="pill" style={value==="annual" ? { background:"#062012", color:"var(--green)", padding: "2px 7px", fontSize: 9 } : { background:"var(--green-bg)", color:"var(--green)", padding: "2px 7px", fontSize: 9 }}>−17%</span>
      </button>
    </div>
  );
}

function FoundingBanner() {
  return (
    <div className="max-w-[1180px] mx-auto mb-10 card flex flex-col md:flex-row items-center justify-between gap-3 p-5 px-7" data-testid="founding-banner"
      style={{ background: "linear-gradient(90deg, rgba(61,220,126,0.10), rgba(61,220,126,0.02))", border: "1px solid var(--green-border)" }}>
      <div className="flex items-center gap-4">
        <Flame size={20} style={{ color: "var(--green)" }} />
        <div>
          <div className="font-bold uppercase tracking-tight text-[14px]">Founding academies — first 10 only</div>
          <div className="label-tiny mt-1">50% off any plan, locked in for 12 months. Help us shape RollOSS.</div>
        </div>
      </div>
      <RequestAccess label="Claim a slot" testid="founding-claim"/>
    </div>
  );
}

function PlanCard({ p, billing }) {
  const price = billing === "annual" ? p.priceAnnual : p.priceMonthly;
  return (
    <div className="card p-7 flex flex-col" style={p.popular ? { border: "1.5px solid var(--green)" } : {}} data-testid={`plan-${p.name.toLowerCase()}`}>
      {p.popular ? (
        <div className="pill pill-active mb-4 self-start" data-testid="plan-popular">Most popular</div>
      ) : <div style={{ height: 24 }} />}
      <div className="text-[22px] font-bold uppercase tracking-tight">{p.name}</div>
      <div className="label-tiny mt-1">{p.sub}</div>
      <div className="flex items-baseline gap-1 mt-5">
        <span className="text-[52px] font-bold tracking-tight leading-none" data-testid={`price-${p.name.toLowerCase()}`}>${price}</span>
        <span style={{ color: "var(--text-dim)" }}>/ month</span>
      </div>
      {billing === "annual" ? (
        <div className="label-tiny mt-2">Billed annually · save ${(p.priceMonthly - p.priceAnnual) * 12}/yr</div>
      ) : (
        <div className="label-tiny mt-2">Billed monthly · ${p.priceAnnual}/mo if billed yearly</div>
      )}
      <div className="mono text-[13px] mt-5" style={{ color: "var(--green)" }}>{p.cap}</div>
      <RequestAccess label="Start 14-day trial" testid={`trial-${p.name.toLowerCase()}`}/>
      <ul className="mt-6 space-y-3 flex-1">
        {p.features.map(f => (
          <li key={f} className="flex items-start gap-2 text-[14px]">
            <Check size={16} style={{ color: "var(--green)", marginTop: 2, flexShrink: 0 }} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Pricing() {
  const [billing, setBilling] = useState("monthly");
  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh" }} data-testid="page-pricing">
      <Nav />
      <section className="px-6 md:px-8 pt-20 pb-10 text-center">
        <div className="label" style={{ color: "var(--green)" }}>Pricing</div>
        <h1 className="hero-title mt-4 mx-auto max-w-[1000px]" style={{ fontSize: "clamp(40px, 6vw, 78px)" }}>
          Pick a plan.<br />Grow your mat.
        </h1>
        <p className="mt-6 mx-auto max-w-[520px] text-[16px] leading-relaxed" style={{ color: "var(--text-dim)" }}>
          Flat monthly pricing per academy. No per-student fees. 14-day free trial on every plan.
        </p>
        <div className="mt-9 flex justify-center">
          <BillingToggle value={billing} onChange={setBilling} />
        </div>
      </section>
      <section className="px-6 md:px-8 pb-8">
        <FoundingBanner />
      </section>
      <section className="px-6 md:px-8 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-[1180px] mx-auto items-stretch">
          {PLANS.map(p => <PlanCard key={p.name} p={p} billing={billing} />)}
        </div>
        <div className="label-tiny text-center mt-10 max-w-[720px] mx-auto leading-relaxed space-y-2">
          <div>All plans include unlimited classes, unlimited coaches, and a student mobile app. Annual plans are billed once upfront. Cancel anytime.</div>
          <div style={{ color: "var(--text-muted)" }}>
            Payments are processed through your own <span style={{color:"var(--text-dim)"}}>Tilopay</span> merchant account.
            Standard Tilopay + Yappy processing fees apply directly to you — RollOSS never touches your revenue.
          </div>
        </div>
      </section>
      <footer className="px-6 md:px-8 py-14 border-t flex flex-col items-center gap-5" style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}>
        <LogoLockup size={28} beltWidth={240} />
        <div className="label-tiny mt-1">© RollOSS · Built for BJJ</div>
      </footer>
    </div>
  );
}
